import * as React from 'react';
import Lottie from 'lottie-react';
import { cn } from '@common/bw-ui/lib/utils';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(onStoreChange: () => void) {
  if (typeof window === 'undefined' || !window.matchMedia) return () => {};
  const query = window.matchMedia(REDUCED_MOTION_QUERY);
  query.addEventListener('change', onStoreChange);
  return () => query.removeEventListener('change', onStoreChange);
}

/**
 * Reports the user's reduced-motion preference, defaulting to `true` on the
 * server. The server default is what makes this SSR-safe: the first (and
 * hydration) render always emits the plain SVG, and useSyncExternalStore
 * re-renders into the animation afterwards rather than tripping a mismatch.
 */
function usePrefersReducedMotion() {
  return React.useSyncExternalStore(
    subscribe,
    // No matchMedia (jsdom, older runtimes) means we can't establish consent
    // to animate, so treat it as reduced rather than animating regardless
    () =>
      window.matchMedia
        ? window.matchMedia(REDUCED_MOTION_QUERY).matches
        : true,
    () => true,
  );
}

/**
 * Deliberately narrow: only the attributes that mean the same thing on both a
 * `div` and an `svg`. These icons are decorative (`aria-hidden`), so they take
 * no event handlers — which is also what lets the same props spread onto
 * either element. `data-*` attributes pass through untyped, as JSX allows.
 */
export interface AnimatedStateIconProps extends React.AriaAttributes {
  /** Lottie animation data, imported from `icons/animations/` */
  animationData: unknown;
  /** Rendered on the server and whenever motion is suppressed */
  fallback: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  className?: string;
  style?: React.CSSProperties;
  id?: string;
  role?: React.AriaRole;
}

/**
 * Shared body of the animated validation icons: a one-shot Lottie that holds
 * its final frame, degrading to the equivalent static icon.
 *
 * The animations carry their own baked-in colours (they can't inherit
 * `currentColor` the way the static SVGs do), so a `text-*` class on these is
 * inert — it still styles the fallback, which is why callers keep passing it.
 */
export function AnimatedStateIcon({
  animationData,
  fallback: Fallback,
  className,
  ...props
}: AnimatedStateIconProps) {
  const prefersReducedMotion = usePrefersReducedMotion();

  if (prefersReducedMotion) {
    return <Fallback className={className} {...props} />;
  }

  return (
    <div className={className} {...props}>
      <Lottie
        animationData={animationData}
        loop={false}
        autoplay
        // tw:flex is load-bearing, not cosmetic. An svg is display:inline by
        // default, so it sits on the text baseline and gets pushed ~4-5px down
        // inside this box; making its parent a flex container blockifies it.
        // The static icons never hit this because there the svg IS the
        // absolutely positioned element, and position:absolute blockifies it.
        // Tailwind preflight would set svg{display:block}, but the Portal runs
        // preflight OFF (Bootstrap reset) - bw-ui must be reset-agnostic.
        className={cn('tw:flex tw:size-full')}
        // The wrapper owns sizing; without this the renderer writes inline
        // width/height onto the svg and ignores the size-* class
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
      />
    </div>
  );
}
